import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from "@/components/ui/sheet";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Sparkles, Check, ArrowRight, Loader2, Trash2, ExternalLink, X, BookOpen } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface OnboardingStep {
  id: string;
  trackId: string;
  title: string;
  body: string | null;
  actionType: string;
  targetRoute: string | null;
  videoUrl: string | null;
  knowledgeSlug: string | null;
  isRequired: boolean;
  sortOrder: number;
}

interface OnboardingTrack {
  id: string;
  name: string;
  description: string | null;
  steps: OnboardingStep[];
}

interface OnboardingBootstrap {
  tracks: OnboardingTrack[];
  completedStepIds: string[];
  gettingStartedDismissedAt: string | null;
  demoSeedPresent: boolean;
}

export function GettingStartedDrawer({ isAdmin }: { isAdmin: boolean }) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  const { data, isLoading } = useQuery<OnboardingBootstrap>({
    queryKey: ["/api/onboarding/tracks"],
    staleTime: 30_000,
  });

  const completedSet = useMemo(() => new Set(data?.completedStepIds || []), [data?.completedStepIds]);

  const allRequiredSteps = useMemo(() => {
    const steps: OnboardingStep[] = [];
    for (const track of data?.tracks || []) {
      for (const s of track.steps || []) {
        if (s.isRequired) steps.push(s);
      }
    }
    return steps;
  }, [data?.tracks]);

  const requiredCount = allRequiredSteps.length;
  const completedRequiredCount = allRequiredSteps.filter((s) => completedSet.has(s.id)).length;
  const progressPercent = requiredCount === 0 ? 0 : Math.round((completedRequiredCount / requiredCount) * 100);
  const allRequiredDone = requiredCount > 0 && completedRequiredCount === requiredCount;

  const markCompleteMutation = useMutation({
    mutationFn: async (stepId: string) => {
      return apiRequest("POST", `/api/onboarding/steps/${stepId}/complete`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/onboarding/tracks"] });
    },
  });

  const seedMutation = useMutation({
    mutationFn: async () => apiRequest("POST", "/api/onboarding/seed-demo-data"),
    onSuccess: () => {
      toast({ title: "Sample data added", description: "Two leads, one client, one invoice — all tagged as demo." });
      queryClient.invalidateQueries({ queryKey: ["/api/onboarding/tracks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
    },
    onError: (e: any) => toast({ title: "Could not seed sample data", description: e?.message || "", variant: "destructive" }),
  });

  const doneMutation = useMutation({
    mutationFn: async () => apiRequest("POST", "/api/onboarding/clear-demo-data", { dismissDrawer: true }),
    onSuccess: () => {
      toast({ title: "Setup complete", description: "Sample data removed. Re-open Walkthroughs from the Knowledge module any time." });
      queryClient.invalidateQueries({ queryKey: ["/api/onboarding/tracks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      setOpen(false);
    },
    onError: (e: any) => toast({ title: "Could not finish setup", description: e?.message || "", variant: "destructive" }),
  });

  const dismissOnlyMutation = useMutation({
    mutationFn: async () => apiRequest("PATCH", "/api/profile/getting-started-dismissed", { dismissed: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/onboarding/tracks"] });
      setOpen(false);
    },
  });

  if (isLoading || !data) return null;
  if (data.gettingStartedDismissedAt) return null;
  if (!data.tracks || data.tracks.length === 0) return null;

  return (
    <div className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-40">
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button
            size="lg"
            className="rounded-full shadow-lg h-12 pr-4 pl-3 gap-2"
            data-testid="button-getting-started-drawer"
          >
            <Sparkles className="w-4 h-4" />
            <span className="hidden sm:inline">Getting Started</span>
            <Badge variant="secondary" className="ml-1 h-5 px-2">
              {completedRequiredCount}/{requiredCount}
            </Badge>
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0">
          <SheetHeader className="p-6 pb-4 border-b">
            <div className="flex items-start justify-between gap-2">
              <div>
                <SheetTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  Getting Started
                </SheetTitle>
                <SheetDescription className="mt-1">
                  Complete each step to set up your workspace. You can revisit these any time from the Knowledge module.
                </SheetDescription>
              </div>
            </div>
            <div className="space-y-1.5 pt-3">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Required progress</span>
                <span className="font-medium" data-testid="text-progress-percent">{progressPercent}%</span>
              </div>
              <Progress value={progressPercent} data-testid="progress-onboarding" />
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {isAdmin && (
              <Card className="p-4 bg-muted/40 border-dashed space-y-2">
                <div className="text-sm font-medium">Sample data</div>
                <p className="text-xs text-muted-foreground">
                  {data.demoSeedPresent
                    ? "We added a few demo rows so you can click around. They're tagged so we can clean them up later."
                    : "Want a tour? Drop in a few sample leads, clients, and an invoice you can safely poke at."}
                </p>
                {!data.demoSeedPresent && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => seedMutation.mutate()}
                    disabled={seedMutation.isPending}
                    data-testid="button-seed-demo-data"
                  >
                    {seedMutation.isPending ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    Add sample data
                  </Button>
                )}
                {data.demoSeedPresent && (
                  <div className="text-xs text-muted-foreground italic">
                    Demo rows are present — they'll be deleted when you click "Done with setup".
                  </div>
                )}
              </Card>
            )}

            {(data.tracks || []).map((track) => (
              <div key={track.id} className="space-y-3">
                <div>
                  <h3 className="text-sm font-semibold">{track.name}</h3>
                  {track.description && (
                    <p className="text-xs text-muted-foreground mt-0.5">{track.description}</p>
                  )}
                </div>
                <div className="space-y-2">
                  {(track.steps || []).map((step) => {
                    const done = completedSet.has(step.id);
                    return (
                      <Card
                        key={step.id}
                        className={`p-3 transition ${done ? "bg-muted/30 opacity-70" : ""}`}
                        data-testid={`onboarding-step-${step.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                            done ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                          }`}>
                            {done ? <Check className="w-3.5 h-3.5" /> : <span className="text-xs font-medium">{step.sortOrder + 1}</span>}
                          </div>
                          <div className="flex-1 min-w-0 space-y-1.5">
                            <div className="flex items-start justify-between gap-2">
                              <div className={`text-sm font-medium ${done ? "line-through" : ""}`}>{step.title}</div>
                              {!step.isRequired && (
                                <Badge variant="outline" className="text-[10px] h-4 px-1.5">Optional</Badge>
                              )}
                            </div>
                            {step.body && (
                              <p className="text-xs text-muted-foreground">{step.body}</p>
                            )}
                            <div className="flex items-center gap-2 pt-1 flex-wrap">
                              {step.actionType === "link" && step.targetRoute && (
                                <Button
                                  asChild
                                  size="sm"
                                  variant={done ? "ghost" : "default"}
                                  className="h-7 text-xs"
                                >
                                  <Link
                                    href={step.targetRoute}
                                    onClick={() => !done && markCompleteMutation.mutate(step.id)}
                                    data-testid={`button-step-action-${step.id}`}
                                  >
                                    Open <ArrowRight className="w-3 h-3 ml-1" />
                                  </Link>
                                </Button>
                              )}
                              {step.actionType === "video" && step.videoUrl && (
                                <Button
                                  asChild
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs"
                                >
                                  <a
                                    href={step.videoUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => markCompleteMutation.mutate(step.id)}
                                    data-testid={`button-step-video-${step.id}`}
                                  >
                                    Watch <ExternalLink className="w-3 h-3 ml-1" />
                                  </a>
                                </Button>
                              )}
                              {step.knowledgeSlug && (
                                <Button asChild size="sm" variant="ghost" className="h-7 text-xs">
                                  <Link
                                    href={`/knowledge/${step.knowledgeSlug}`}
                                    data-testid={`button-step-knowledge-${step.id}`}
                                  >
                                    <BookOpen className="w-3 h-3 mr-1" /> Read more
                                  </Link>
                                </Button>
                              )}
                              {!done && step.actionType !== "link" && step.actionType !== "video" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs"
                                  onClick={() => markCompleteMutation.mutate(step.id)}
                                  disabled={markCompleteMutation.isPending}
                                  data-testid={`button-step-mark-${step.id}`}
                                >
                                  <Check className="w-3 h-3 mr-1" /> Mark done
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t p-4 space-y-2 bg-muted/20">
            {isAdmin && (
              <Button
                className="w-full"
                onClick={() => doneMutation.mutate()}
                disabled={doneMutation.isPending}
                variant={allRequiredDone ? "default" : "secondary"}
                data-testid="button-done-with-setup"
              >
                {doneMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Done with setup {data.demoSeedPresent && "(removes sample data)"}
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs text-muted-foreground"
              onClick={() => dismissOnlyMutation.mutate()}
              disabled={dismissOnlyMutation.isPending}
              data-testid="button-hide-drawer"
            >
              <X className="w-3.5 h-3.5 mr-1.5" />
              Hide for now (find this in Knowledge later)
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
